// H-26: hiring a candidate.
//
// The flow is four short steps and one of them is the point: the DECISION
// RATIONALE (L-3). A score that effectively decides is an automated decision
// under GDPR and the UK rules, and a human who only confirms the AI's output is
// not enough; what makes this a human decision is that a person wrote down why.
// The backend refuses the call without it.
//
// What happens next is worth saying on the screen, because it surprises people:
// the email on the candidate's EXISTING account changes to the work email, so
// their assessment results come with them as their starting levels, and they
// sign in with the password they already set. There is no second invitation.

import React, { useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import {
  ActionButton,
  BODY,
  DANGER,
  Field,
  FONT,
  INPUT_SX,
  LINE,
  Note,
  Segmented,
  SelectField,
  SURFACE,
  TEXT
} from '../capability-setup/designSystem';
import { RationaleField } from './hiringParts';
import type { CopyBlock, HiringCandidateView, HiringRoundView } from '../../../../api/hiring';

const HireDialog: React.FC<{
  open: boolean;
  candidate: HiringCandidateView;
  round: HiringRoundView;
  teams: Array<{ id: string; name: string }>;
  roles: Array<{ id: string; name: string; teamId: string }>;
  canHireAsAdmin: boolean;
  tooltip: CopyBlock | null;
  busy?: boolean;
  error?: string | null;
  onHire: (input: {
    rationale: string;
    workEmail: string;
    teamId: string;
    roleId: string;
    access: 'learner' | 'admin';
  }) => void;
  onClose: () => void;
}> = ({ open, candidate, round, teams, roles, canHireAsAdmin, tooltip, busy = false, error, onHire, onClose }) => {
  const [rationale, setRationale] = useState('');
  const [workEmail, setWorkEmail] = useState('');
  const [teamId, setTeamId] = useState(round.teamId);
  const [roleId, setRoleId] = useState(round.roleId);
  const [access, setAccess] = useState<'learner' | 'admin'>('learner');

  const emailLooksRight = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(workEmail.trim());
  const missing = !emailLooksRight
    ? 'Enter their company email'
    : rationale.trim().length < 10
    ? 'Say briefly why you are making this decision'
    : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: SURFACE, border: `1px solid ${LINE}`, borderRadius: '8px' } }}
    >
      <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, color: TEXT }}>
            Hire {candidate.name}
          </Typography>
          <Typography sx={{ ...BODY, mt: '4px' }}>
            {candidate.status === 'started'
              ? "They haven't finished their assessment. They keep the same account, so only what they have completed so far becomes their starting capability levels."
              : 'They keep the same account, so their assessment results become their starting capability levels.'}
          </Typography>
        </Box>

        <RationaleField value={rationale} onChange={setRationale} tooltip={tooltip} />

        <Field
          label="Their company email"
          required
          help="This becomes their Dosen sign-in email. They get one email asking them to verify it; their existing password still works."
        >
          <Box
            component="input"
            type="email"
            value={workEmail}
            placeholder="first.last@yourcompany.com"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWorkEmail(e.target.value)}
            sx={INPUT_SX}
          />
        </Field>

        <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <Field label="Team">
              <SelectField
                label="Team"
                options={teams}
                value={teamId}
                placeholder="Choose a team"
                onChange={(id) => {
                  setTeamId(id);
                  const first = roles.find((r) => r.teamId === id);
                  if (first) setRoleId(first.id);
                }}
              />
            </Field>
          </Box>
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <Field label="Role">
              <SelectField
                label="Role"
                options={roles.filter((r) => r.teamId === teamId)}
                value={roleId}
                placeholder="Choose a role"
                onChange={setRoleId}
              />
            </Field>
          </Box>
        </Box>

        <Field label="Access">
          <Segmented
            label="What access they get"
            options={[
              { value: 'learner', label: 'Learner' },
              ...(canHireAsAdmin ? [{ value: 'admin' as const, label: 'Admin' }] : [])
            ]}
            value={access}
            onChange={setAccess}
          />
        </Field>

        <Note>
          Signing in with your company&apos;s single sign-on starts working once they have clicked the verification
          link. Until then they sign in with the password they set during their assessment, and you can correct a typo
          in the email from their profile.
        </Note>

        {error && (
          <Typography sx={{ ...BODY, color: DANGER }} role="alert">
            {error}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <ActionButton onClick={onClose}>Cancel</ActionButton>
          <ActionButton
            primary
            disabled={Boolean(missing) || busy}
            reason={missing || undefined}
            onClick={() => onHire({ rationale: rationale.trim(), workEmail: workEmail.trim(), teamId, roleId, access })}
          >
            {busy ? 'Hiring...' : `Hire ${candidate.firstName || candidate.name}`}
          </ActionButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default HireDialog;
